import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import usePrevious from '../../../../../Hooks/usePrevious';
import sendNotification from '../../../../../notifications/sendNotification';
import joinList from '../../../../../utils/joinList';
import lexicalToString from '../../../../../utils/lexicalToString';
import { useUserInfoData } from '../../../../Hooks/useUserInfo';
import useIsWindowVisible from '../../useIsWindowVisible';
import { SessionComplianceChecklistItem } from '../useSessionCompliance/type';

export default function useComplianceNotifications(sessionInProgress: boolean, sessionTopics: SessionComplianceChecklistItem[]) {
  const previous = usePrevious(sessionTopics);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('notifications');
  const { t: tCommon } = useTranslation('common');
  const { isVisible } = useIsWindowVisible();
  const notificationSettings = useUserInfoData().appSettings?.notifications;

  const newlyCompleted = React.useMemo(
    () => sessionTopics.filter((topic) => {
      if (!topic.isCompleted) return false;
      const prevTopic = previous.find((item) => (item.textLower === topic.textLower));
      return !prevTopic?.isCompleted;
    }),
    [sessionTopics, previous],
  );

  const newlyIncluded = React.useMemo(
    () => sessionTopics.filter((topic) => !previous?.find((prevTopic) => (prevTopic.textLower === topic.textLower))),
    [sessionTopics, previous],
  );

  const previousCompleted = usePrevious(newlyCompleted);
  const previousIncluded = usePrevious(newlyIncluded);

  React.useEffect(() => {
    if (!sessionInProgress) return;
    if (previousCompleted !== newlyCompleted && newlyCompleted.length > previousCompleted.length) {
      newlyCompleted.forEach((topic) => {
        const title = topic.topic.text;
        enqueueSnackbar(t('completed-compliance-task.title', { title }), { variant: 'success' });
      });
    }
  }, [previousCompleted, newlyCompleted, sessionInProgress]);
  React.useEffect(() => {
    if (!sessionInProgress) return;
    if (previousIncluded !== newlyIncluded && newlyIncluded.length > previousIncluded.length) {
      newlyIncluded.forEach((topic) => {
        const title = topic.topic.text;

        if (!isVisible && notificationSettings?.notifyOnNewMandatoryTopic) {
          const commands = joinList(topic.topic.projectCommands?.items.map((item) => `"${item.command.text}"`), tCommon('or'));
          let body;
          if (commands) {
            body = t('new-compliance-task.body', { commands });
          }
          if (topic.complianceExample) {
            const instructions = lexicalToString(topic.complianceExample).join('\n');
            body = `"${instructions}"`;
          }
          sendNotification(
            t('new-compliance-task.title', { title }),
            {
              body,
              actions: [{ action: 'open', title: t('new-compliance-task.open') }],
            },
          );
        }
        enqueueSnackbar(t('new-compliance-task.title', { title }), { variant: 'info' });
      });
    }
  }, [previousIncluded, newlyIncluded, isVisible, sessionInProgress]);
}
